import React, { useState } from 'react';
import { BidLineItem } from '../../models/bid';
import { LineItemCard } from './LineItemCard';
import { formatCurrency } from '../../utils/lineItemHelpers';

interface LineItemTableProps {
  lineItems: BidLineItem[];
  hasLaborMaterialSplit: boolean;
  totalBidAmount?: number;
}

export const LineItemTable: React.FC<LineItemTableProps> = ({
  lineItems,
  hasLaborMaterialSplit,
  totalBidAmount
}) => {
  const [expandedParents, setExpandedParents] = useState<Set<string>>(new Set());

  const toggleParentExpanded = (itemId: string) => {
    setExpandedParents(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  // Calculate totals
  const calculateTotals = () => {
    let totalLabor = 0;
    let totalMaterial = 0;
    let totalOther = 0;
    let totalCost = 0;

    lineItems.forEach(item => {
      totalLabor += item.laborCost || 0;
      totalMaterial += item.materialCost || 0;
      totalOther += item.otherCosts || 0;
      totalCost += item.totalCost || 0;
    });

    return { totalLabor, totalMaterial, totalOther, totalCost };
  };

  const totals = calculateTotals();

  return (
    <div className="space-y-4">
      {/* Line items as cards */}
      <div className="space-y-2">
        {lineItems.map((item, index) => {
          const itemId = item.id || `item-${index}`;
          const isParentExpanded = expandedParents.has(itemId);

          return (
            <LineItemCard
              key={itemId}
              item={item}
              index={index}
              level={0}
              isExpanded={isParentExpanded}
              onToggleExpand={() => toggleParentExpanded(itemId)}
            />
          );
        })}
      </div>

      {/* Total Summary */}
      <div className="bg-gradient-to-r from-gray-50 to-white border-2 border-gray-300 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="text-base font-semibold text-gray-700">Total</div>
          <div className="flex items-center gap-6">
            {hasLaborMaterialSplit && (
              <div className="flex gap-4 text-xs">
                <div className="text-right">
                  <div className="text-gray-500 mb-0.5">Labor</div>
                  <div className="font-semibold text-gray-900">{formatCurrency(totals.totalLabor)}</div>
                </div>
                <div className="text-right">
                  <div className="text-gray-500 mb-0.5">Materials</div>
                  <div className="font-semibold text-gray-900">{formatCurrency(totals.totalMaterial)}</div>
                </div>
                <div className="text-right">
                  <div className="text-gray-500 mb-0.5">Other</div>
                  <div className="font-semibold text-gray-900">{formatCurrency(totals.totalOther)}</div>
                </div>
              </div>
            )}
            <div className="text-right">
              <div className="text-2xl font-bold text-gray-900">{formatCurrency(totalBidAmount || totals.totalCost)}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
